import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Button } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The heart on a product card — the way a shopper keeps a product without
 * leaving the listing:
 *
 *   <FavouritesButton aria-label={label}>
 *     <FavouritesMark variant={FavouritesMarkVariant.SAVED} render={<Heart />} />
 *   </FavouritesButton>
 *
 * **The control and the mark are two parts**, because the mark travels and the
 * control does not: the same heart stands in the tool row beneath a product's
 * buy box, where the target is `@vs-shop/commerce-ui/productPanelTools`'s and
 * the colouring follows that row's own rule rather than this one.
 *
 * **Whether a product is saved is an appearance here, and the opposite one
 * there.** On a card the saved heart is filled and coloured at rest, because
 * the card is a glance; in the tool row the same fact is muted at rest and
 * warns only under a pointer, because that row sits beneath the thing a shopper
 * came to do. One recipe cannot serve both, and pretending otherwise is how a
 * shared part starts lying.
 *
 * **Nothing about favourites crosses into this package.** Which lists exist,
 * whether the product is on one, what pressing this opens and what it is called
 * are all the tenant's; the control takes `aria-label`, `onClick` and the rest
 * natively, and a dialog or a menu renders through it.
 *
 * Server-first: both parts are plain components.
 */

/**
 * The control: a square icon target the card's action row can hold.
 *
 * `group` is load-bearing — the mark answers to a pointer over the whole
 * target, not over itself — and the ground is pinned against the hover the
 * button would otherwise take, because the colour change belongs to the heart.
 */
const favouritesButtonClasses = 'group hover:bg-button-secondary';

export type FavouritesButtonProps = ComponentProps<typeof Button>;

/**
 * The card's favourites control.
 *
 * `type="button"` by default and overridable: a card stands inside listing
 * forms, and a control that submitted one would be a surprise.
 */
export const FavouritesButton = ({ className, type, ...props }: FavouritesButtonProps) => (
  <Button
    type={type ?? 'button'}
    size="icon"
    className={clsxm(favouritesButtonClasses, className)}
    {...props}
  />
);

/** Whether the product is already kept, as the card paints it. */
export const favouritesMarkVariants = cva('size-5', {
  variants: {
    variant: {
      /** Already on a list: filled and coloured at rest, readable at a glance. */
      saved: 'fill-destructive text-destructive',
      /** On no list yet: the card's own ink, colouring only under a pointer. */
      unsaved: 'group-hover:text-destructive',
      /** The opt-out: a tenant colouring the mark itself. */
      unstyled: '',
    },
  },
});

type FavouritesMarkVariantName = NonNullable<
  VariantProps<typeof favouritesMarkVariants>['variant']
>;

export type FavouritesMarkVariant = FavouritesMarkVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const FavouritesMarkVariant = {
  /** Already kept. */
  SAVED: 'saved',
  /** Not kept yet. */
  UNSAVED: 'unsaved',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, FavouritesMarkVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type FavouritesMarkVariantProps = { readonly variant: FavouritesMarkVariant };

export type FavouritesMarkProps = ComponentProps<'div'> &
  FavouritesMarkVariantProps & {
    /** The heart to paint instead of the neutral `<div>`. */
    readonly render?: RenderElement;
  };

/**
 * The heart itself.
 *
 * The drawing is the tenant's, painted through this rather than wrapped by it:
 * icon sets are a tenant's until they are a platform's, and the size is the
 * control's own decision rather than every call site's.
 */
export const FavouritesMark = ({ className, variant, render, ...props }: FavouritesMarkProps) => (
  <RenderedPart
    dataSlot="favourites-mark"
    className={clsxm(favouritesMarkVariants({ variant }), className)}
    render={render}
    {...props}
  />
);
